import ngramSimilarity from './ngramSimilarity'
import jaroWinklerSimilarity from './jaroWinklerSimilarity'
import calculateAcronymScore from './calculateAcronymScore'

/**
 * Comprehensive field scoring with multiple algorithms
 */
const calculateAdvancedFieldScore = (query, fieldValue, fieldWeight = 1) => {
  if (!query || !fieldValue) return { score: 0, type: 'none' }

  const queryNorm = query.toLowerCase().trim()
  const fieldNorm = fieldValue.toLowerCase().trim()

  // 1. Exact match (perfect score)
  if (fieldNorm === queryNorm) {
    return { score: 1.0 * fieldWeight, type: 'exact' }
  }

  // 2. Starts with (very high score)
  if (fieldNorm.startsWith(queryNorm)) {
    const ratio = queryNorm.length / fieldNorm.length
    return { score: (0.95 + ratio * 0.05) * fieldWeight, type: 'prefix' }
  }

  // 3. Contains (high score with position bonus)
  if (fieldNorm.includes(queryNorm)) {
    const position = fieldNorm.indexOf(queryNorm)
    const ratio = queryNorm.length / fieldNorm.length
    const positionBonus = Math.max(0, (fieldNorm.length - position) / fieldNorm.length * 0.1)
    return { score: (0.8 + ratio * 0.1 + positionBonus) * fieldWeight, type: 'substring' }
  }

  // 4. Word boundary matches
  const words = fieldNorm.split(/\s+/)
  for (const word of words) {
    if (word.startsWith(queryNorm)) {
      const ratio = queryNorm.length / word.length
      return { score: (0.85 + ratio * 0.05) * fieldWeight, type: 'word_prefix' }
    }
    if (word.includes(queryNorm)) {
      const ratio = queryNorm.length / word.length
      return { score: (0.7 + ratio * 0.05) * fieldWeight, type: 'word_substring' }
    }
  }

  // 5. Advanced acronym matching
  const acronymScore = calculateAcronymScore(queryNorm, fieldNorm)
  if (acronymScore > 0.5) {
    return { score: acronymScore * fieldWeight, type: 'acronym' }
  }

  // 6. Jaro-Winkler similarity (excellent for names)
  const jaroScore = jaroWinklerSimilarity(queryNorm, fieldNorm)
  if (jaroScore > 0.7) {
    return { score: jaroScore * 0.8 * fieldWeight, type: 'jaro_winkler' }
  }

  // 7. N-gram similarity (handles typos and rearrangements)
  const ngramScore = ngramSimilarity(queryNorm, fieldNorm)
  if (ngramScore > 0.4) {
    return { score: ngramScore * 0.7 * fieldWeight, type: 'ngram' }
  }

  // 8. Try on individual words with different algorithms
  let bestWordScore = 0
  let bestWordType = 'none'

  for (const word of words) {
    // Jaro-Winkler on words
    const wordJaro = jaroWinklerSimilarity(queryNorm, word)
    if (wordJaro > bestWordScore && wordJaro > 0.6) {
      bestWordScore = wordJaro
      bestWordType = 'word_jaro'
    }

    // N-gram on words
    const wordNgram = ngramSimilarity(queryNorm, word)
    if (wordNgram > bestWordScore && wordNgram > 0.5) {
      bestWordScore = wordNgram
      bestWordType = 'word_ngram'
    }
  }

  if (bestWordScore > 0) {
    return { score: bestWordScore * 0.6 * fieldWeight, type: bestWordType }
  }

  return { score: 0, type: 'none' }
}

export default calculateAdvancedFieldScore
